<template>
  <div class="home">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <clientNavSelector></clientNavSelector>
      </el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: `/${currentClientId}/requests` }">Requests</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: `/${currentClientId}/requests/new-review-request` }">New Review Schedule Request
      </el-breadcrumb-item>
    </el-breadcrumb>
    <hia-loading v-if="loading"></hia-loading>
    <div v-if="!loading">
      <el-row :gutter="40" style="margin-top: 40px;">
        <el-col :span="5">
          <div>
            <el-steps id="requestSteps" direction="vertical" :active="activeStep" finish-status="success">
              <el-step title="Request Basics">
                <div slot="description">
                  <p>Please select Acute and/or Professional Fee Review Type Options</p>
                  <p>Please input a Review Objective.</p>
                  <p>Please select a preferred timeframe for your review to be performed.</p>
                </div>
              </el-step>
              <el-step title="Add Review Records">
                <div slot="description">
                  <p>Expand each patient type to add or delete records.</p>
                  <p>Certain patient types have different options available. Please read notes if available.</p>
                  <p>Once you have added records to the request, please continue next.</p>
                </div>
              </el-step>
              <el-step title="Confirm">
                <div slot="description">
                  <p>Confirm your request and choose Submit Request to send to HIA.</p>
                </div>
              </el-step>
            </el-steps>
          </div>
        </el-col>
        <el-col :span="19">

          <transition name="fade" mode="out-in">
            <!-- Step 0 -->
            <div key="step0" v-if="activeStep === 0">
              <el-form class="step0Form" label-position="top" label-width="200px" ref="newScheduleRequest" :rules="rules" :model="newScheduleRequest" @submit.prevent.native>
                <el-checkbox-group v-model="newScheduleRequest.reviewType">

                  <!-- Using nested form items with no labels here so that the custom validations can show the red messages in the right spots -->
                  <el-form-item prop="reviewType" label="Review Type">
                    <el-form-item prop="acuteReviewTypeId">
                      <div>
                        <el-checkbox label="Acute" @change="handleReviewTypeCheckbox($event, 'Acute')">Include Acute</el-checkbox>
                      </div>
                      <div>
                        <el-select v-show="newScheduleRequest.reviewType.some(x => x === 'Acute')" style="margin-top: 10px; margin-left: 5px;"
                          placeholder="Select Acute Review Type" v-model="newScheduleRequest.acuteReviewTypeId" size="mini">
                          <el-option v-for="(item, index) in reviewTypes.filter(x => x.serviceLine === 'Acute')" :value="item.reviewTypeId" :label="item.type" :key="index">
                          </el-option>
                        </el-select>
                      </div>
                    </el-form-item>
                    <el-form-item prop="proReviewTypeId" style="margin-bottom: 0px;">
                      <div>
                        <el-checkbox label="Professional Fee" @change="handleReviewTypeCheckbox($event, 'Pro')">Include Professional Fee</el-checkbox>
                      </div>
                      <div>
                        <el-select v-show=" newScheduleRequest.reviewType.some(x=> x === 'Professional Fee')" style="margin-top: 10px; margin-left: 5px;"
                          placeholder="Select Pro Review Type" v-model="newScheduleRequest.proReviewTypeId" size="mini">
                          <el-option v-for="(item, index) in reviewTypes.filter(x => x.serviceLine === 'Pro')" :value="item.reviewTypeId" :label="item.type" :key="index">
                          </el-option>
                        </el-select>
                      </div>
                    </el-form-item>
                  </el-form-item>
                </el-checkbox-group>
                <el-form-item prop="reviewDescription" label="Review Objectives">
                  <el-input type="textarea" :rows="3" class="reviewDescription" placeholder="Explain objectives of review" v-model="newScheduleRequest.reviewDescription">
                  </el-input>
                </el-form-item>
                <el-form-item prop="dateRange" label="Requested Review Dates">
                  <el-date-picker v-model="newScheduleRequest.dateRange" type="daterange" range-separator="To" start-placeholder="Start date" end-placeholder="End date"
                    :picker-options="dateRangeOptions" format="MM/dd/yyyy" value-format="MM/dd/yyyy">
                  </el-date-picker>
                </el-form-item>
              </el-form>

              <el-button type="primary" id="next" style="margin-top: 20px;" @click="completeStepZero">Next <i style="padding-left: 5px;" class="el-icon-arrow-down"></i>
              </el-button>
            </div>

            <!-- Step 1 -->
            <div key="step1" v-if="activeStep === 1">
              <ReviewRequestAcuteRecordDetails :loading="loading" :reviewRequest="newScheduleRequest" @addRecords="addRecords" @addProRecords="addProRecords"
                @toggleProSelectedBy="toggleProSelectedBy">
              </ReviewRequestAcuteRecordDetails>

              <el-button plain style="margin-top: 30px; margin-bottom: 20px" @click="activeStep--">
                Modify Previous <i style="padding-left: 5px;" class="el-icon-arrow-up"></i>
              </el-button>

              <el-button title="Add coders before moving to the next step" type="primary" :disabled="totalReviewRecords() === 0" style="margin-top: 30px;"
                @click="completeStepOne">
                Next <i style="padding-left: 5px;" class="el-icon-arrow-down"></i>
              </el-button>
            </div>

            <!-- Step 2 -->
            <div key="step2" v-if="activeStep === 2">
              <h4 class="heading">Please Confirm Request</h4>

              <el-row>
                <el-col :span="4">
                  <span class="demoTitle">Review Type:</span>
                </el-col>
                <el-col :span="8">
                  <span class="demoData">{{getReviewTypeConfirmationText()}}</span>
                </el-col>
              </el-row>
              <el-row>
                <el-col :span="4">
                  <span class="demoTitle">Objectives:</span>
                </el-col>
                <el-col :span="8">
                  <span class="demoData">{{newScheduleRequest.reviewDescription}}</span>
                </el-col>
              </el-row>
              <el-row>
                <el-col :span="4">
                  <span class="demoTitle">Review Dates:</span>
                </el-col>
                <el-col :span="8">
                  <span class="demoData">starting {{newScheduleRequest.dateRange[0]}} and ending
                    {{newScheduleRequest.dateRange[1]}}</span>
                </el-col>
              </el-row>

              <div v-if="newScheduleRequest.reviewType.find(x => x === 'Acute')">
                <el-table id="confirmTable" ref="confirmTable" class="top-margin" :data="newScheduleRequest.records" :row-class-name="hideDisplayRows">
                  <el-table-column label="Patient Type" width="180">
                    <template slot-scope="scope">
                      <span>{{ scope.row.title }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Options">
                    <template slot-scope="scope">
                      <span v-html="getConfirmationOptions(scope.row)"></span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Number of Records" width="180">
                    <template slot-scope="scope">
                      <span>{{ getConfirmationRecords(scope.row) }}</span>
                    </template>
                  </el-table-column>
                  <template slot="empty">
                    <p> No Acute Records</p>
                  </template>
                </el-table>

                <p class="totalRecords" style="text-align: right; padding-right: 20px;">Total Acute Records:
                  {{totalAcuteRecords()}}</p>

                <p class="totalRecordsSub" style="text-align: right; padding-right: 20px;" v-if="hasProEm">
                  Includes Pro E&M on Select Records
                </p>
              </div>
              <div v-if="newScheduleRequest.reviewType.find(x => x === 'Professional Fee')">
                <el-table id="proRecordConfirmTable" ref="proRecordConfirmTable" class="top-margin" :data="newScheduleRequest.proRecords.records">
                  <el-table-column v-if="newScheduleRequest.profeeByProvider" label="Specialty" width="180">
                    <template slot-scope="scope">
                      <span>{{ scope.row.specialty }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column :label="newScheduleRequest.profeeByProvider ? 'Providers' : 'Coders'">
                    <template slot-scope="scope">
                      <span>{{ scope.row.numProviders }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Details">
                    <template slot-scope="scope">
                      <span v-html="getProRecordConfirmDetails(scope.row)"></span>
                    </template>
                  </el-table-column>
                  <el-table-column label="Total Records" width="180">
                    <template slot-scope="scope">
                      <span>{{ getProRecordConfirmRecords(scope.row) }}</span>
                    </template>
                  </el-table-column>
                  <template slot="empty">
                    <p> No Professional Fee Records</p>
                  </template>
                </el-table>

                <p class="totalRecords" style="text-align: right; padding-right: 20px;">Total Professional Fee Records:
                  {{totalProfeeRecords()}}</p>
              </div>

              <p class="reviewNotes">Additional Notes:</p>
              <el-input type="textarea" :rows="4" placeholder="Notes" v-model="newScheduleRequest.notes">
              </el-input>
              <el-button plain style="margin-top: 30px; margin-bottom: 20px" @click="activeStep--">
                Modify Previous <i style="padding-left: 5px;" class="el-icon-arrow-up"></i>
              </el-button>

              <el-button type="primary" style="margin-top: 30px;" @click="completeStepTwo">
                Submit Request
              </el-button>
              <br />
              <hia-loading style="margin-top: 40px;" v-if="submitting"></hia-loading>
            </div>
            <!-- Step 3 -->
            <div key="step3" v-if="activeStep === 3">
              <h4>Thank you, we have received your request. We will review and confirm the availability of our staff. A
                final schedule will be sent prior to the start date.</h4>
              <router-link :to="`/${currentClientId}/requests`">
                <el-button type="primary">Return to Request List</el-button>
              </router-link>
            </div>
          </transition>

        </el-col>
        <el-col :span="4">
          <ul class="actionButtons" style="margin-top: 0">
            <li>
              <!-- <el-button type="primary">New Request</el-button> -->
            </li>
          </ul>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import ReviewRequestAcuteRecordDetails from './ReviewRequestAcuteRecordDetails'

  export default {
    data: function () {
      return {
        activeStep: 0,
        loading: true,
        submitting: false,
        dateRangeOptions: {
          disabledDate(time) {
            let today = new Date(Date.now() - (24 * 60 * 60 * 1000))
            return time.getTime() < today.getTime()
          }
        },
        newScheduleRequest: {
          location: '1',
          dateRange: [],
          reviewDescription: '',
          profeeByProvider: true,
          acuteReviewTypeId: null,
          proReviewTypeId: null,
          notes: null,
          reviewType: [],
          proRecords: {
            records: []
          },
          records: []
        },
        rules: {
          location: [
            { required: true, message: 'Please Select a Location', trigger: 'change' }
          ],
          dateRange: [
            { required: true, message: 'Please Select a Date Range', trigger: 'change' }
          ],
          reviewType: [
            {
              required: true,
              validator: (rule, value, callback) => {
                if (this.newScheduleRequest.reviewType.length === 0) {
                  callback(new Error('Please Select Acute and/or Pro Review Options'))
                  return
                }
                callback()
              },
              trigger: 'change'
            }
          ],
          acuteReviewTypeId: [
            {
              required: true,
              validator: (rule, value, callback) => {
                if (this.newScheduleRequest.reviewType.some(x => x === 'Acute') && !this.newScheduleRequest.acuteReviewTypeId) {
                  callback(new Error('Please Select Acute Review Type'))
                  return
                }
                callback()
              },
              trigger: 'change'
            }
          ],
          proReviewTypeId: [
            {
              required: true,
              validator: (rule, value, callback) => {
                if (this.newScheduleRequest.reviewType.some(x => x === 'Professional Fee') && !this.newScheduleRequest.proReviewTypeId) {
                  callback(new Error('Please Select Pro Review Type'))
                  return
                }
                callback()
              },
              trigger: 'change'
            }
          ],
          reviewDescription: [
            { required: true, message: 'Please Input a Review Objective', trigger: 'change' }
          ]
        }
      }
    },
    name: 'NewReviewRequest',
    methods: {
      ...mapActions('requests/reviews', ['POST_REVIEW_SCHEDULE_REQUEST']),
      ...mapActions('requests/reviews', ['GET_REVIEWVALIDATIONS', 'CHECK_REVIEW_EXISTS', 'GET_REVIEW_TYPES']),
      ...mapActions('requests/', ['GET_SPECIALTIES']),
      async initialize() {
        this.loading = true
        await Promise.all([this.GET_REVIEW_TYPES(), this.GET_REVIEWVALIDATIONS(), this.GET_SPECIALTIES()])
        this.loading = false
      },
      completeStepZero() {
        this.$refs['newScheduleRequest'].validate((valid) => {
          if (valid) {
            // clear review types if unchecked
            this.submitting = true
            this.CHECK_REVIEW_EXISTS(this.newScheduleRequest).then(() => {
              this.activeStep++
              this.submitting = false
            }, error => {
              let message = error.response.data ? error.response.data.message : error
              this.$message({
                type: 'error',
                message: message,
                duration: 10000
              })
              this.submitting = false
            })
          }
        })
      },
      completeStepOne() {
        this.activeStep++
      },
      completeStepTwo() {
        this.submitting = true
        let payload = this.newScheduleRequest
        this.POST_REVIEW_SCHEDULE_REQUEST(payload).then(() => {
          this.activeStep++
          this.submitting = false
        }, error => {
          let message = error.response.data ? error.response.data.message : error
          this.$message({
            type: 'error',
            message: message,
            duration: 50
          })
          this.submitting = false
        })
      },
      handleReviewTypeCheckbox(e, type) {
        // when unchecking the box, clear the Review Type Id fields and any red messages
        if (type === 'Acute' && !e) {
          this.newScheduleRequest.acuteReviewTypeId = null
          this.$refs['newScheduleRequest'].validateField(['acuteReviewTypeId'])
        }

        if (type === 'Pro' && !e) {
          this.newScheduleRequest.proReviewTypeId = null
          this.$refs['newScheduleRequest'].validateField(['proReviewTypeId'])
        }
      },
      addRecords(payload) {
        this.newScheduleRequest.records = payload.records
      },
      addProRecords(payload) {
        this.newScheduleRequest.proRecords = payload.records
      },
      toggleProSelectedBy() {
        this.newScheduleRequest.proRecords.records = []
      },
      getConfirmationOptions(row) {
        let optionText = []
        let instructionText = []

        if (row.poa) {
          optionText.push('POA')
        }
        if (row.aprDrg) {
          optionText.push('APR-DRG')
        }
        if (row.level2) {
          optionText.push('Level 2')
        }

        if (row.includePCS) {
          optionText.push('Include PCS')
        }
        if (row.ii) {
          optionText.push('Add I&I')
        }

        if (row.em) {
          optionText.push('Add E&M')
        }

        if (row.si) {
          optionText.push('Add S&I')
        }

        if (row.proem) {
          optionText.push('Add Professional E&M')
        }

        if (row.recurringRecords) {
          if (row.recurringAccount) {
            instructionText.push(`<br />Recurring Account:  ${row.recurringAccount}`)
          }
        }

        if (row.instructions) {
          instructionText.push(`<br />Special Instructions:  ${row.instructions}`)
        }

        if (row.serviceInstructions) {
          instructionText.push(`<br />Services Review:  ${row.serviceInstructions}`)
        }
        return `${optionText.join(', ')} ${instructionText.join('')}`
      },
      getConfirmationRecords(row) {
        if (row.type === 'ed') {
          return (row.records + row.facility + row.professional + row.both)
        }
        return row.records
      },
      hideDisplayRows(row) {
        if (row.row.type === 'ed') {
          return (row.row.records + row.row.facility + row.row.professional + row.row.both) > 0 ? 'show' : 'hide'
        }
        return row.row.records > 0 ? 'show' : 'hide'
      },
      totalReviewRecords() {
        let count = 0
        for (let i = 0; i < this.newScheduleRequest.records.length; i++) {
          count += this.newScheduleRequest.records[i].records
          if (this.newScheduleRequest.records[i].type === 'ed') {
            count += (this.newScheduleRequest.records[i].facility + this.newScheduleRequest.records[i].professional + this.newScheduleRequest.records[i].both)
          }
        }

        let length = this.newScheduleRequest.proRecords.records.length
        let proRecordsCount = 0
        let item
        if (length > 0) {
          for (let i = 0; i < length; i++) {
            item = this.newScheduleRequest.proRecords.records[i]
            proRecordsCount += (item.numProviders * item.numEM) + (item.numProviders * item.numCPT)
          }
        }
        count += proRecordsCount

        return count
      },
      totalAcuteRecords() {
        let count = 0
        for (let i = 0; i < this.newScheduleRequest.records.length; i++) {
          count += this.newScheduleRequest.records[i].records
          if (this.newScheduleRequest.records[i].type === 'ed') {
            count += (this.newScheduleRequest.records[i].facility + this.newScheduleRequest.records[i].professional + this.newScheduleRequest.records[i].both)
          }
        }
        return count
      },
      totalProfeeRecords() {
        let length = this.newScheduleRequest.proRecords.records.length
        let proRecordsCount = 0
        let item
        if (length > 0) {
          for (let i = 0; i < length; i++) {
            item = this.newScheduleRequest.proRecords.records[i]
            proRecordsCount += (item.numProviders * item.numEM) + (item.numProviders * item.numCPT)
          }
        }
        return proRecordsCount
      },
      getProRecordConfirmRecords(row) {
        return (row.numProviders * row.numEM) + (row.numProviders * row.numCPT)
      },
      getProRecordConfirmDetails(row) {
        let optionText = []
        let placeService = []

        if (row.numEM) {
          optionText.push(`EM: ${(row.numEM)}`)
        }
        if (row.numCPT) {
          optionText.push(`CPT: ${(row.numCPT)}`)
        }

        if (row.isClinic) {
          placeService.push('Clinic')
        }
        if (row.isHospital) {
          placeService.push('Hospital')
        }

        return placeService.length ? `${optionText.join(', ')} <br /> ${placeService.join(', ')} ` : `${optionText.join(', ')} `
      },
      getReviewTypeConfirmationText() {
        let arr = this.newScheduleRequest.reviewType.map(x => {
          if (x === 'Acute') {
            let acuteReviewType = this.reviewTypes.find(x => x.reviewTypeId === this.newScheduleRequest.acuteReviewTypeId)
            if (acuteReviewType) {
              return `Acute - ${acuteReviewType.type}`
            } else {
              return 'Acute'
            }
          }

          if (x === 'Professional Fee') {
            let proReviewType = this.reviewTypes.find(x => x.reviewTypeId === this.newScheduleRequest.proReviewTypeId)
            if (proReviewType) {
              return `Professional Fee - ${proReviewType.type}`
            } else {
              return 'Professional Fee'
            }
          }
        })

        return arr.join(', ')
      }
    },
    created: function () {
      this.initialize()
    },
    computed: {
      ...mapState('user/', ['currentClient', 'currentClientId']),
      ...mapState('requests/reviews', ['reviewValidations', 'reviewTypes']),
      ...mapState('requests/', ['specialties']),
      hasProEm: function () {
        let proEM = []
        proEM = this.newScheduleRequest.records.filter(function (item) {
          return item.proem === true
        })
        return (proEM.length > 0)
      }
    },
    components: {
      ReviewRequestAcuteRecordDetails
    }
  }
</script>

<style scoped>
  >>>tr .cell {
    font-weight: 500;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .totalRecords {
    font-size: 14px;
  }

  .totalRecordsSub {
    font-size: 12px;
  }

  .reviewNotes {
    margin: 40px 0px 0px 0px;
    padding: 0px 0px 0px 0px;
    font-size: 14px;
  }

  /* ACTION BUTTONS */
  .actionButtonsIcon {
    font-size: 18px;
  }

  /* EL STEPS */
  #requestSteps {
    background: rgba(238, 238, 238, 0.5);
    border-radius: 5px;
    padding: 5px 2px 5px 2px;
  }

  #requestSteps>>>.el-step {
    /* min-height: 150px; */
    flex-basis: auto !important;
  }

  #requestSteps>>>.el-step__main {
    padding-bottom: 10px;
  }

  /* step waiting*/
  #requestSteps>>>.el-step__description.is-wait {
    padding-right: 0px;
  }

  #requestSteps>>>.el-step__description.is-wait p {
    margin: 0px 0px 10px 0px !important;
    font-weight: normal !important;
    padding-right: 0px;
  }

  /* step in process*/
  #requestSteps>>>.el-step__description.is-process {
    padding-right: 0px;
  }

  #requestSteps>>>.el-step__description.is-process p {
    margin: 0px 0px 10px 0px;
    padding-right: 0px;
    font-weight: normal !important;
    color: #606266;
  }

  #requestSteps>>>.el-step__title.is-process,
  #requestSteps>>>.el-step__head.is-process {
    color: #005695;
  }

  #requestSteps>>>.el-step__head.is-process .el-step__line {
    background-color: #005695;
  }

  #requestSteps>>>.el-step__head.is-process .el-step__icon.is-text {
    border: 2px solid #005695;
  }

  /* step success */
  #requestSteps>>>.el-step__description.is-success {
    padding-right: 0px;
  }

  #requestSteps>>>.el-step__description.is-success p {
    margin: 0px 0px 10px 0px;
    padding-right: 0px;
    font-weight: normal !important;
    color: #c0c4cc;
  }

  #requestSteps>>>.el-step__title.is-success,
  #requestSteps>>>.el-step__head.is-success {
    color: #7cbe5f;
  }

  #requestSteps>>>.el-step__head.is-success .el-step__line {
    background-color: #7cbe5f;
  }

  #requestSteps>>>.el-step__head.is-success .el-step__icon.is-text {
    border: 2px solid #7cbe5f;
  }

  /* ADD CALEDNAR */
  .addCalendar>>>.el-form-item__content {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 0px 0px 15px 0px;
  }

  /* request button */
  .requestButton {
    font-size: 12px;
    font-weight: 500;
  }

  .requestButtonIcons {
    font-size: 22px;
    float: right;
    margin: -5px 0px 0px 0px;
    padding: 0px 0px 0px 0px;
    height: 14px;
    width: 14px;
  }

  >>>.cell {
    word-break: inherit !important;
  }

  >>>.el-table__empty-block {
    min-height: 39px;
  }

  >>>.table-list {
    display: table-cell;
    vertical-align: middle;
    list-style-type: none;
    padding: 0px;
  }

  /* FORM STYLE */
  .step0Form>>>.reviewDescription {
    width: 350px;
  }

  /* CONFIRM TABLE */
  #confirmTable>>>tr.hide {
    display: none;
  }

  /* TEXT AREA PLACEHOLDER */
  textarea::-webkit-input-placeholder {
    font-family: Roboto, sans-serif !important;
    color: orange !important;
  }

  /* DEMOGRAPHICS SECTION */
  .demoTitle {
    color: #909399;
    font-size: 14px;
  }

  .demoData {
    color: #606266;
    font-size: 14px;
  }
</style>
